import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DesktopDatePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/it';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React from 'react';

dayjs.extend(customParseFormat);

/**
 * Parses and normalizes various date input formats into a valid Dayjs object.
 * Supports Dayjs instances, standard ISO strings (YYYY-MM-DD), and localized Italian formats (DD/MM/YYYY).
 *
 * @param {any} val - The raw date value to normalize.
 * @returns {Dayjs | null} A valid Dayjs instance or null if the input is invalid or empty.
 */
export const normalizeDate = (val: any): Dayjs | null => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val.isValid() ? val : null;

  if (typeof val === 'string') {
    let parsed = dayjs(val, 'YYYY-MM-DD', true);
    if (parsed.isValid()) return parsed;

    parsed = dayjs(val, 'DD/MM/YYYY', true);
    if (parsed.isValid()) return parsed;

    parsed = dayjs(val);
    if (parsed.isValid()) return parsed;
  }

  const finalParsed = dayjs(val);
  return finalParsed.isValid() ? finalParsed : null;
};

/**
 * Configuration properties for the FormAndToolbarDatePicker component.
 */
export interface UniversalDatePickerProps {
  /** Field identifier used for standard form setups */
  name?: string;
  /** Current field value used in standalone controlled environments */
  value?: any;
  /** Callback triggered when the date changes, returning a 'YYYY-MM-DD' string or null */
  onChange?: (value: string | null) => void;
  /** Input object injected automatically when used as a React Final Form adapter */
  input?: {
    name: string;
    value: any;
    onChange: (value: string | null) => void;
  };
  /** Meta state object injected automatically by React Final Form for validation tracking */
  meta?: {
    touched?: boolean;
    error?: string;
  };
  /** Floating label text for the input field */
  label?: string;
  /** Custom text displayed when the input field is empty */
  placeholder?: string;
  /** If true, disables the date picker interactions completely */
  disabled?: boolean;
  /** If true, prevents users from selecting any date in the future */
  noFuture?: boolean;
  /** If true, hides or disables the calendar popup trigger icon button */
  disableOpenPicker?: boolean;
  /** Minimum CSS layout width constraint for the outer form box element */
  minWidth?: number | string;
  /** Optional secondary callback that exposes the raw native Dayjs object on change */
  setValue?: (value: Dayjs | null) => void;
  /** Explicit error state override flag, ideal for external validation frameworks */
  error?: boolean;
  /** Explicit helper or descriptive validation error message displayed below the input */
  helperText?: string;
}

/**
 * A hybrid, high-utility MUI Date Picker wrapper designed to operate polymorphically.
 * It detects its runtime environment automatically to handle state in two distinct ways:
 * 1. **Form Mode**: Integrates smoothly with React Final Form as a form field adapter.
 * 2. **Standalone Mode**: Operates as a generic controlled input field inside toolbars and filter rows.
 *
 * It automatically enforces a localized Italian calendar locale, compact layout heights (40px),
 * and handles database-friendly string serializations (YYYY-MM-DD) out of the box.
 */
export const UniversalDatePicker: React.FC<UniversalDatePickerProps> = ({
  input,
  meta,
  name,
  value,
  onChange,
  label,
  placeholder = 'Seleziona data',
  disabled = false,
  noFuture = false,
  disableOpenPicker = false,
  minWidth = 200,
  setValue,
  error,
  helperText,
  ...rest
}) => {
  // Extract state polymorphically from form architecture or standard controlled props
  const resolvedName = input?.name || name;
  const resolvedValue = input ? input.value : value;
  const resolvedOnChange = input ? input.onChange : onChange;

  // Consolidate validation error states across both runtime context types
  const isError = !!(meta?.touched && meta?.error) || !!error;
  const resolvedHelperText = meta?.touched ? meta?.error : helperText || '';

  const handleDateChange = (newValue: Dayjs | null) => {
    const formattedValue = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : null;

    if (resolvedOnChange) {
      resolvedOnChange(formattedValue);
    }
    if (setValue) {
      setValue(newValue);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <DesktopDatePicker<Dayjs>
        format="DD/MM/YYYY"
        value={normalizeDate(resolvedValue)}
        disableFuture={noFuture}
        disabled={disabled}
        disableOpenPicker={disableOpenPicker}
        onChange={handleDateChange}
        slots={{ openPickerIcon: CalendarMonthIcon }}
        slotProps={{
          textField: {
            ...rest,
            name: resolvedName,
            size: 'small',
            label: label,
            placeholder: placeholder,
            error: isError,
            helperText: resolvedHelperText,
            fullWidth: true,
            InputLabelProps: { shrink: true },
            sx: {
              minWidth: minWidth,
              width: minWidth,

              '& .MuiInputLabel-root': { display: 'none' },
              '& .MuiOutlinedInput-notchedOutline': { legend: { width: 0 } },

              '& .MuiOutlinedInput-root': {
                borderRadius: 2, // Arrotondamento coerente a 8px
                height: '40px', // Altezza allineata al millimetro con i bottoni e le select
                backgroundColor: 'background.paper',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' }
                }
              }
            }
          }
        }}
      />
    </LocalizationProvider>
  );
};
